import React from 'react';
import { Box, Typography } from '@mui/material';
import { ContractorDetailsProps } from '../types/contractor.types';
import { getFileType } from '../utils/fileUtils';
import DocumentActions from './DocumentActions';
import DocumentViewer from './DocumentViewer';

const ContractorDetails: React.FC<ContractorDetailsProps> = ({
  contractor,
  addresses,
  documents,
  selectedDocumentId,
  onDocumentSelect,
  onDocumentAction,
  onDownloadDocument,
  onVerifyContract
}) => {
  const primaryAddress = addresses.find((address) => address.isPrimary);
  const selectedDoc = documents.find((doc) => doc.documentId === selectedDocumentId) || null;

  const formatAddress = (address: any) => {
    if (!address) return 'N/A';
    return `${address.unit || ''} ${address.building || ''} ${address.street || ''}, ${address.city?.name || 'N/A'}, ${address.province?.name || 'N/A'}, ${address.zipCode || 'N/A'}`;
  };

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Contractor Details
      </Typography>

      {/* Personal Information */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Typography variant="body1" sx={{ flex: 1 }}>
          <strong>First Name:</strong> {contractor.firstName}
        </Typography>
        <Typography variant="body1" sx={{ flex: 1 }}>
          <strong>Birthday:</strong> {contractor.birthday ? new Date(contractor.birthday).toLocaleDateString() : 'N/A'}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Typography variant="body1" sx={{ flex: 1 }}>
          <strong>Middle Name:</strong> {contractor.middleName || 'N/A'}
        </Typography>
        <Typography variant="body1" sx={{ flex: 1 }}>
          <strong>Phone Number:</strong> {contractor.phoneNumber || 'N/A'}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Typography variant="body1" sx={{ flex: 1 }}>
          <strong>Last Name:</strong> {contractor.lastName}
        </Typography>
        <Typography variant="body1" sx={{ flex: 1 }}>
          <strong>Email:</strong> {contractor.email || 'N/A'}
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 2 }}>
        <strong>Address: </strong>
        {formatAddress(primaryAddress)}
      </Typography>

      {/* Documents Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Documents Submitted
      </Typography>

      <DocumentActions
        documents={documents}
        selectedDocumentId={selectedDocumentId}
        onDocumentSelect={onDocumentSelect}
        onApprove={() => onDocumentAction(1)}
        onClarify={() => onDocumentAction(3)}
        onReject={() => onDocumentAction(2)}
        onDownload={onDownloadDocument}
        onVerify={onVerifyContract}
      />

      {/* Document Viewer */}
      <DocumentViewer document={selectedDoc} />

      {/* Document Info */}
      {selectedDoc && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: '4px' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>File Type:</strong> {getFileType(selectedDoc.mimeType, selectedDoc.filename)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>MIME Type:</strong> {selectedDoc.mimeType}
          </Typography>
          <Typography variant="body2">
            <strong>Status:</strong> {selectedDoc.status || 'Pending Review'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ContractorDetails;